import { useEffect, useState } from 'react'
import { useAdminApi } from '../api'
import { t } from '../i18n'
import { useLang } from '../LangContext'
import { formatDate, formatDateTime, localeFor } from '../../utils/format'
import { categoriaLabel } from '../../panel/i18n'
import { EmptyState } from '../../styles/EmptyState'
import { Spinner } from '../../styles/Spinner'
import styles from './UsoPage.module.css'

export interface ResumenUso {
  dias: number
  activos: { hoy: number; semana: number; mes: number }
  totales: { peticiones: number; errores_api: number; errores_web: number; p50: number | null; p95: number | null }
  diario: { dia: string; usuarios: number; peticiones: number; errores: number }[]
  rendimiento: { metodo: string; ruta: string; peticiones: number; p50: number; p95: number; max: number; errores: number }[]
  usuarios: { id: string; nombre: string; roles: string[]; ultimo_acceso: string | null; dias_activo: number; peticiones: number }[]
  errores: { fecha: string; origen: 'api' | 'web'; metodo: string | null; ruta: string; estado: number | null; mensaje: string | null; usuario: string | null }[]
  equipos: {
    id: string
    nombre: string
    categoria: string
    ultima_asistencia: string | null
    marcas_periodo: number
    ultimo_minutaje: string | null
    minutaje_periodo: number
  }[]
}

const PERIODOS = [7, 30, 90]

/** Un elemento por día del periodo (también los días sin actividad, a 0), del más antiguo a hoy. */
export function rellenarDias(diario: ResumenUso['diario'], dias: number, hoy = new Date()): ResumenUso['diario'] {
  const porDia = new Map(diario.map((d) => [d.dia, d]))
  return Array.from({ length: dias }, (_, i) => {
    const fecha = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (dias - 1 - i))
    const dia = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`
    return porDia.get(dia) ?? { dia, usuarios: 0, peticiones: 0, errores: 0 }
  })
}

function ms(valor: number | null): string {
  if (valor === null) return '—'
  return valor >= 1000 ? `${(valor / 1000).toFixed(1)} s` : `${valor} ms`
}

// Umbrales orientativos para colorear tiempos de respuesta: < 0,5 s bien, < 2 s aceptable, más, lento.
function claseTiempo(valor: number | null): string | undefined {
  if (valor === null) return undefined
  return valor < 500 ? 'pill pill-success' : valor < 2000 ? 'pill pill-warning' : 'pill pill-danger'
}

// Petición del club (2026-10-31): "necesito estadísticas de uso de la app, no sé si se usa,
// rendimiento, si hay fallos, tengo 0 visibilidad". Resumen de GET /admin/uso: actividad (quién entra y
// cuándo), rendimiento por petición, últimos fallos y actividad por equipo con los datos que ya había.
export function UsoPage() {
  const api = useAdminApi()
  const lang = useLang()
  const locale = localeFor(lang)
  const [dias, setDias] = useState(30)
  const [datos, setDatos] = useState<ResumenUso | null>(null)

  useEffect(() => {
    setDatos(null)
    api.get(`/admin/uso?dias=${dias}`).then(setDatos)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dias])

  const diario = datos ? rellenarDias(datos.diario, dias) : []
  const maxDia = Math.max(1, ...diario.map((d) => d.usuarios))

  return (
    <div className={styles.page}>
      <h2>{t('usoEstado', lang)}</h2>
      <p className={styles.ayuda}>{t('usoIntro', lang)}</p>
      <div className="tabs">
        {PERIODOS.map((n) => (
          <button key={n} onClick={() => setDias(n)} disabled={n === dias}>
            {t('usoUltimosDias', lang).replace('{n}', String(n))}
          </button>
        ))}
      </div>

      {datos === null ? (
        <Spinner />
      ) : (
        <>
          <section className={styles.kpis} aria-label={t('usoEstado', lang)}>
            <div className={styles.kpi}>
              <span>{t('usoActivosHoy', lang)}</span>
              <strong>{datos.activos.hoy}</strong>
            </div>
            <div className={styles.kpi}>
              <span>{t('usoActivosSemana', lang)}</span>
              <strong>{datos.activos.semana}</strong>
            </div>
            <div className={styles.kpi}>
              <span>{t('usoActivosMes', lang)}</span>
              <strong>{datos.activos.mes}</strong>
            </div>
            <div className={styles.kpi}>
              <span>{t('usoPeticiones', lang)}</span>
              <strong>{datos.totales.peticiones}</strong>
            </div>
            <div className={styles.kpi}>
              <span>{t('usoRespuestaTipica', lang)}</span>
              <strong>{ms(datos.totales.p50)}</strong>
              <small>
                {t('usoRespuestaLenta', lang)}: {ms(datos.totales.p95)}
              </small>
            </div>
            <div className={styles.kpi} data-alerta={datos.totales.errores_api + datos.totales.errores_web > 0 ? 'true' : undefined}>
              <span>{t('usoErrores', lang)}</span>
              <strong>{datos.totales.errores_api + datos.totales.errores_web}</strong>
              <small>
                {t('usoErroresDetalle', lang)
                  .replace('{api}', String(datos.totales.errores_api))
                  .replace('{web}', String(datos.totales.errores_web))}
              </small>
            </div>
          </section>

          <section className={styles.seccion}>
            <h3>{t('usoPorDia', lang)}</h3>
            {datos.diario.length === 0 ? (
              <EmptyState text={t('usoSinDatos', lang)} icon="📊" />
            ) : (
              <div className={styles.grafico} role="img" aria-label={t('usoPorDia', lang)}>
                {diario.map((d) => (
                  <div
                    key={d.dia}
                    className={styles.barra}
                    title={`${formatDate(d.dia, locale)}: ${d.usuarios} · ${t('usoPeticiones', lang)} ${d.peticiones}`}
                  >
                    <span className={styles.valor}>{d.usuarios}</span>
                    <span className={styles.relleno} style={{ height: `${(d.usuarios / maxDia) * 100}%` }} />
                    <span className={styles.dia}>{d.dia.slice(8, 10)}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className={styles.seccion}>
            <h3>{t('usoUsuarios', lang)}</h3>
            <div className="table-scroll">
              <table className="sticky-first-col" aria-label={t('usoUsuarios', lang)}>
                <thead>
                  <tr>
                    <th>{t('nombre', lang)}</th>
                    <th>{t('rol', lang)}</th>
                    <th>{t('usoUltimoAcceso', lang)}</th>
                    <th>{t('usoDiasActivo', lang)}</th>
                    <th>{t('usoPeticiones', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.usuarios.map((u) => (
                    <tr key={u.id}>
                      <td>{u.nombre}</td>
                      <td>{u.roles.join(', ')}</td>
                      <td>{u.ultimo_acceso ? formatDateTime(u.ultimo_acceso, locale) : <em className={styles.nunca}>{t('usoNunca', lang)}</em>}</td>
                      <td>{u.dias_activo}</td>
                      <td>{u.peticiones}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.seccion}>
            <h3>{t('usoEquipos', lang)}</h3>
            <p className={styles.ayuda}>{t('usoEquiposAyuda', lang)}</p>
            <div className="table-scroll">
              <table className="sticky-first-col" aria-label={t('usoEquipos', lang)}>
                <thead>
                  <tr>
                    <th>{t('equipos', lang)}</th>
                    <th>{t('usoUltimaAsistencia', lang)}</th>
                    <th>{t('usoMarcas', lang)}</th>
                    <th>{t('usoUltimoMinutaje', lang)}</th>
                    <th>{t('usoFilasMinutaje', lang)}</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.equipos.map((e) => (
                    <tr key={e.id}>
                      <td>
                        {e.nombre} <small className={styles.ayuda}>{categoriaLabel(e.categoria, lang)}</small>
                      </td>
                      <td>{e.ultima_asistencia ? formatDateTime(e.ultima_asistencia, locale) : '—'}</td>
                      <td>{e.marcas_periodo}</td>
                      <td>{e.categoria === 'eskola' ? '' : e.ultimo_minutaje ? formatDateTime(e.ultimo_minutaje, locale) : '—'}</td>
                      <td>{e.categoria === 'eskola' ? '' : e.minutaje_periodo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className={styles.seccion}>
            <h3>{t('usoRendimiento', lang)}</h3>
            {datos.rendimiento.length === 0 ? (
              <EmptyState text={t('usoSinDatos', lang)} icon="⏱️" />
            ) : (
              <div className="table-scroll">
                <table className="sticky-first-col" aria-label={t('usoRendimiento', lang)}>
                  <thead>
                    <tr>
                      <th>{t('usoRuta', lang)}</th>
                      <th>{t('usoPeticiones', lang)}</th>
                      <th>{t('usoRespuestaTipica', lang)}</th>
                      <th>p95</th>
                      <th>{t('usoMaximo', lang)}</th>
                      <th>{t('usoErrores', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.rendimiento.map((r) => (
                      <tr key={`${r.metodo} ${r.ruta}`}>
                        <td>
                          <code>
                            {r.metodo} {r.ruta}
                          </code>
                        </td>
                        <td>{r.peticiones}</td>
                        <td>{ms(r.p50)}</td>
                        <td>
                          <span className={claseTiempo(r.p95)}>{ms(r.p95)}</span>
                        </td>
                        <td>{ms(r.max)}</td>
                        <td>{r.errores || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className={styles.seccion}>
            <h3>{t('usoListaErrores', lang)}</h3>
            {datos.errores.length === 0 ? (
              <EmptyState text={t('usoSinErrores', lang)} icon="✅" />
            ) : (
              <div className="table-scroll">
                <table aria-label={t('usoListaErrores', lang)}>
                  <thead>
                    <tr>
                      <th>{t('usoFecha', lang)}</th>
                      <th>{t('usoUsuarios', lang)}</th>
                      <th>{t('usoDonde', lang)}</th>
                      <th>{t('usoMensaje', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.errores.map((e, i) => (
                      <tr key={`${e.fecha}-${i}`}>
                        <td className={styles.nowrap}>{formatDateTime(e.fecha, locale)}</td>
                        <td>{e.usuario ?? '—'}</td>
                        <td>
                          <span className="pill">{t(e.origen === 'web' ? 'usoNavegador' : 'usoServidor', lang)}</span>{' '}
                          <code>
                            {e.metodo ? `${e.metodo} ` : ''}
                            {e.ruta}
                            {e.estado ? ` (${e.estado})` : ''}
                          </code>
                        </td>
                        <td className={styles.mensaje}>{e.mensaje ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}
